/**
 * Lightweight client-side image processing for service request uploads.
 * Validates type/size, rescales to a max dimension, re-encodes as JPEG.
 *
 * Storage strategy: base64 data URLs embedded in the MongoDB doc.
 * After compression each image is ~150–400 KB, well within doc size limits
 * for a soft-launch product. Avoids the need for S3/CDN setup.
 */

export const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];
export const ACCEPTED_EXTENSIONS_LABEL = 'JPG, PNG';
export const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB raw upload
export const MAX_IMAGES = 3;
const MAX_DIMENSION = 1600;     // px on the longest side post-resize
const JPEG_QUALITY = 0.82;

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const loadImage = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.src = dataUrl;
  });

/**
 * Validates a single file. Throws a user-friendly Error if invalid.
 */
export const validateImageFile = (file) => {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    throw new Error(`Unsupported file type. Please upload ${ACCEPTED_EXTENSIONS_LABEL}.`);
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(`File too large. Maximum size is 5 MB.`);
  }
};

/**
 * Validate, rescale, re-encode as JPEG base64 data URL. Resolves to a string.
 */
export const compressImageToDataUrl = async (file) => {
  validateImageFile(file);
  const rawDataUrl = await readFileAsDataURL(file);
  const img = await loadImage(rawDataUrl);

  const { width, height } = img;
  const longest = Math.max(width, height);
  const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
  const targetWidth = Math.round(width * scale);
  const targetHeight = Math.round(height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  // White background prevents PNG transparency turning black when re-encoded as JPEG
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, targetWidth, targetHeight);
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
};
